/**
 * Device Token Service
 * Manages device identification for risk-based OTP challenges
 * Generates UUID once and persists to localStorage
 */

import { v4 as uuidv4 } from 'uuid';

const DEVICE_TOKEN_KEY = 'ic_device_token';
const UUID_V4_REGEX = /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

const generateUUID = () => uuidv4();

/**
 * Get existing device token from localStorage or create new one
 * @returns {string} Device token UUID
 */
export const getOrCreateDeviceToken = () => {
  try {
    let token = localStorage.getItem(DEVICE_TOKEN_KEY);

    // If no token exists or invalid format, create new one
    if (!token || !UUID_V4_REGEX.test(token)) {
      token = generateUUID();
      localStorage.setItem(DEVICE_TOKEN_KEY, token);
    }

    return token;
  } catch {
    // Fallback if localStorage unavailable
    return generateUUID();
  }
};

/**
 * Get current device token without creating if missing
 * @returns {string|null} Device token or null
 */
export const getDeviceToken = () => {
  try {
    return localStorage.getItem(DEVICE_TOKEN_KEY) || null;
  } catch {
    return null;
  }
};

/**
 * Create and store a new device token (used for re-enrollment)
 * @returns {string} New device token
 */
export const createDeviceToken = () => {
  try {
    const token = generateUUID();
    localStorage.setItem(DEVICE_TOKEN_KEY, token);
    return token;
  } catch {
    return generateUUID();
  }
};

/**
 * Clear device token from localStorage (called on logout)
 */
export const clearDeviceToken = () => {
  try {
    localStorage.removeItem(DEVICE_TOKEN_KEY);
  } catch {
    // no-op
  }
};
